import { useHandler } from "@redotech/react-util/hook";
import { getAvailableTimezones } from "@redotech/redo-common-model/timezone-utils";
import { SegmentTimeOfDayOperator } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  RcoSegmentStep,
  rcoTimeOfDaySegmentSchema,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import { OperatorSelector } from "@redotech/redo-web-conversion/conversion/operator-selector";
import {
  BadgeRedoListItem,
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { TemporalDayOfWeek } from "@redotech/util/temporal";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";
import { TimePicker } from "../time-picker";

function segmentTimeOfDayOperatorToText(): Record<
  SegmentTimeOfDayOperator,
  string
> {
  return {
    [SegmentTimeOfDayOperator.BEFORE]: "before",
    [SegmentTimeOfDayOperator.AFTER]: t("at or after"),
  };
}

function weekdayToText(): Record<TemporalDayOfWeek, string> {
  return {
    [TemporalDayOfWeek.MONDAY]: t("Monday"),
    [TemporalDayOfWeek.TUESDAY]: t("Tuesday"),
    [TemporalDayOfWeek.WEDNESDAY]: t("Wednesday"),
    [TemporalDayOfWeek.THURSDAY]: t("Thursday"),
    [TemporalDayOfWeek.FRIDAY]: t("Friday"),
    [TemporalDayOfWeek.SATURDAY]: t("Saturday"),
    [TemporalDayOfWeek.SUNDAY]: t("Sunday"),
  };
}

export const TimeOfDayInspectorContent = memo(
  function TimeOfDayInspectorContent({ step }: { step: RcoSegmentStep }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoTimeOfDaySegmentSchema.parse(step.segment),
      [step],
    );

    const timeString = `${segment.time.hh.toString().padStart(2, "0")}:${segment.time.mm.toString().padStart(2, "0")}`;
    const timezoneDisplay = segment.ianaTimezone.replace(/_/g, " ");
    const weekdaysDisplay = segment.weekdays
      .map((weekday) => weekdayToText()[weekday])
      .join(", ");

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Condition")}
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          Current time is {segmentTimeOfDayOperatorToText()[segment.operator]}{" "}
          {timeString} on {weekdaysDisplay} in timezone {timezoneDisplay}
        </Text>
      </Flex>
    );
  },
);

export const EditableTimeOfDayInspectorContent = memo(
  function EditableTimeOfDayInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoTimeOfDaySegmentSchema.parse(step.segment),
      [step],
    );

    const availableTimezones = useMemo(() => getAvailableTimezones(), []);

    const timezoneOptions: TextRedoListItem<string>[] = useMemo(() => {
      return availableTimezones.map((timezone) => ({
        type: "text",
        id: timezone,
        value: timezone,
        text: timezone.replace(/_/g, " "),
      }));
    }, [availableTimezones]);

    const selectedTimezoneItem = useMemo(() => {
      return timezoneOptions.find(
        (option) => option.value === segment.ianaTimezone,
      );
    }, [timezoneOptions, segment.ianaTimezone]);

    const weekdayOptions: BadgeRedoListItem<TemporalDayOfWeek>[] =
      useMemo(() => {
        return Object.values(TemporalDayOfWeek)
          .filter((value) => typeof value === "number")
          .map((weekday) => ({
            type: "badge",
            id: weekday.toString(),
            value: weekday,
            badge: { text: weekdayToText()[weekday], color: "gray" },
          }));
      }, []);

    const selectedWeekdays = useMemo(() => {
      return weekdayOptions.filter((option) =>
        segment.weekdays.includes(option.value),
      );
    }, [weekdayOptions, segment.weekdays]);

    const handleOperatorSelected = useHandler(
      (operator: SegmentTimeOfDayOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleHoursChange = useHandler((hh: number) => {
      const updatedSegment = { ...segment, time: { ...segment.time, hh } };
      setStep({ ...step, segment: updatedSegment });
    });

    const handleMinutesChange = useHandler((mm: number) => {
      const updatedSegment = { ...segment, time: { ...segment.time, mm } };
      setStep({ ...step, segment: updatedSegment });
    });

    const handleTimezoneSelected = useHandler(
      ({ item }: { item: RedoListItem<string> }) => {
        const updatedSegment = { ...segment, ianaTimezone: item.value };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleWeekdaysSelected = useHandler(
      (selectedItems: BadgeRedoListItem<TemporalDayOfWeek>[]) => {
        const weekdays = selectedItems.map((item) => item.value);
        const updatedSegment = { ...segment, weekdays };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("Current time is")}
          </Text>
        </Flex>
        <Flex>
          <OperatorSelector<SegmentTimeOfDayOperator>
            availableOptions={Object.values(SegmentTimeOfDayOperator)}
            onOperatorSelected={handleOperatorSelected}
            operatorToText={segmentTimeOfDayOperatorToText()}
            selectedOperator={segment.operator}
          />
        </Flex>
        <TimePicker
          hours={segment.time.hh}
          minutes={segment.time.mm}
          setHours={handleHoursChange}
          setMinutes={handleMinutesChange}
        />
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("on these days")}
        </Text>
        <RedoTagsDropdownInput<TemporalDayOfWeek>
          options={weekdayOptions}
          placeholder={t("Select days")}
          selectedOptions={selectedWeekdays}
          setSelectedOptions={handleWeekdaysSelected}
          size="xs"
        />
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("in timezone")}
        </Text>
        <RedoSingleSelectDropdownInput<string>
          fitToAnchor={false}
          options={timezoneOptions}
          optionSelected={handleTimezoneSelected}
          selectedItem={selectedTimezoneItem}
        />
      </>
    );
  },
);
